import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, AlertTriangle } from 'lucide-react';

interface BusinessSubscriptionBadgeStaticProps {
  subscription?: {
    status: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    plan_name: string | null;
  } | null;
  className?: string;
}

const BusinessSubscriptionBadgeStatic = ({ subscription, className = "" }: BusinessSubscriptionBadgeStaticProps) => {
  if (!subscription) {
    return (
      <Badge variant="outline" className={`${className} text-muted-foreground`}>
        No Subscription
      </Badge>
    );
  }

  const getSubscriptionBadge = () => {
    const { status, current_period_end, cancel_at_period_end, plan_name } = subscription;
    
    const now = new Date();
    const periodEnd = new Date(current_period_end);
    
    // Check if subscription is active based on status and period end
    const hasActiveSubscription = (
      ['active', 'trialing', 'past_due'].includes(status) ||
      (periodEnd > now && !['unpaid', 'incomplete_expired', 'incomplete'].includes(status))
    );

    // Determine if we're in a grace period (canceled but still active until period end)
    const isInGracePeriod = status === 'canceled' && periodEnd > now;
    
    if (isInGracePeriod) {
      return (
        <Badge variant="outline" className={`${className} bg-amber-50 text-amber-700 border-amber-200`}>
          <AlertTriangle className="h-3 w-3 mr-1" />
          Canceled - Active
        </Badge>
      );
    }
    
    if (status === 'active') {
      return (
        <Badge variant="outline" className={`${className} bg-green-50 text-green-700 border-green-200`}>
          <CheckCircle className="h-3 w-3 mr-1" />
          Active {plan_name ? `(${plan_name})` : ''}
        </Badge>
      );
    }

    if (status === 'past_due') {
      return (
        <Badge variant="outline" className={`${className} bg-amber-50 text-amber-700 border-amber-200`}>
          <AlertTriangle className="h-3 w-3 mr-1" />
          Past Due
        </Badge>
      );
    }

    if (status === 'trialing') {
      return (
        <Badge variant="outline" className={`${className} bg-blue-50 text-blue-700 border-blue-200`}>
          <Clock className="h-3 w-3 mr-1" />
          Trial
        </Badge>
      );
    }

    // For other active statuses
    if (hasActiveSubscription) {
      return (
        <Badge variant="outline" className={`${className} bg-green-50 text-green-700 border-green-200`}>
          <CheckCircle className="h-3 w-3 mr-1" />
          Subscribed
        </Badge>
      );
    }

    // Inactive subscription
    return (
      <Badge variant="outline" className={`${className} text-muted-foreground`}>
        Inactive
      </Badge>
    );
  };

  return getSubscriptionBadge();
};

export default BusinessSubscriptionBadgeStatic;